import React from 'react';
import type { EmailBulkSendSummaryFieldsFragment } from '~/api/graphql';
import { LONGEST_TIMER_DELAY_MS } from '~/lib/scheduled-send';
import { batchProgressPolling, batchTally } from './outbox-bulk-send-summary';

/**
 * Runs `poll` for as long and as often as the batch's progress can change, by
 * batchProgressPolling. Nothing runs before the batch has loaded.
 */
export const useBatchProgressPolling = (
  batch: EmailBulkSendSummaryFieldsFragment | null | undefined,
  poll: () => void
) => {
  const waiting = batch ? batchTally(batch).waiting : 0;
  const firstSendAtMs = batch?.firstSendAt ? new Date(batch.firstSendAt).getTime() : 0;
  const sentAtMs = batch?.sentAt ? new Date(batch.sentAt).getTime() : 0;
  const isWaitingForMailbox = !!batch?.isWaitingForMailbox;
  // Held in a ref so a caller's callback, rebuilt as its rows load, doesn't restart the timers.
  const pollRef = React.useRef(poll);
  React.useEffect(() => {
    pollRef.current = poll;
  });
  // Time alone can change how to read: a scheduled batch's first send time arriving, or the
  // hand-off reads after Send ending. A timer re-renders the caller then, to pick again.
  const [pollingRechecks, recheckPolling] = React.useReducer((count: number) => count + 1, 0);
  React.useEffect(() => {
    const untilFirstSendMs = firstSendAtMs - Date.now();
    if (untilFirstSendMs <= 0) {
      return;
    }
    const timer = window.setTimeout(
      recheckPolling,
      Math.min(untilFirstSendMs, LONGEST_TIMER_DELAY_MS)
    );
    return () => window.clearTimeout(timer);
  }, [firstSendAtMs]);
  React.useEffect(() => {
    const polling = batchProgressPolling(
      { waiting, firstSendAtMs, sentAtMs, isWaitingForMailbox },
      Date.now()
    );
    if (!polling) {
      return;
    }
    let pollTimer: number | undefined;
    const startTimer = window.setTimeout(
      () => {
        pollTimer = window.setInterval(() => pollRef.current(), polling.intervalMs);
      },
      Math.min(polling.startInMs, LONGEST_TIMER_DELAY_MS)
    );
    const stopTimer =
      polling.stopInMs === null
        ? undefined
        : window.setTimeout(() => {
            window.clearInterval(pollTimer);
            recheckPolling();
          }, polling.stopInMs);
    return () => {
      window.clearTimeout(startTimer);
      window.clearTimeout(stopTimer);
      window.clearInterval(pollTimer);
    };
  }, [waiting, firstSendAtMs, sentAtMs, isWaitingForMailbox, pollingRechecks]);
};
